import { ofType } from 'redux-observable';
import { concat, forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, map, withLatestFrom } from 'rxjs/operators';
import { notification } from '../../components';
import { ErrorMsg, notifyErrorOrFallback } from '../../settings/models/Error';
import { FieldActions, UserActions } from '../actions';
import { Action } from '../app/basicModels';
import { getMeasurementPhenomena } from './catalog';
import { getFieldsByProperty } from '../field/repository';
import { RegionActions } from '../region/actions';
import { getRegionsByProperty } from '../region/service';
import { getUsersByOrg } from '../user/service';
import actions from './actions';
import { DeleteMeasurementRequestParams, LoadMeasurementsRequestParams, Measurement, Monitoring } from './models';
import {
  deleteMeasureByInspectionGroup,
  deleteMeasurement,
  deleteMeasurementByInspection,
  editMeasurementValue,
  editMeasurementValueByInspection,
  editMeasures,
  getMeasurements
} from './repository';
import {
  buildMeasurements,
  getEditMeasurementByInspectionRequest,
  getEditMeasurementRequest,
  getEditMeasuresRequest,
  getMeasuresErrorMessage
} from './service';
import { AppState } from '../redux.model';

export const handleLoadMeasurements = (action$, state$) =>
  action$.pipe(
    ofType(actions.LOAD_MEASUREMENTS_BY_PROPERTY),
    map((action: Action<LoadMeasurementsRequestParams>) => action.payload),
    concatMap((payload: LoadMeasurementsRequestParams) => {
      return getMeasurements(payload).pipe(
        map(response => response.data),
        map((monitorings: { monitorings: Monitoring[] }) => {
          return monitorings;
        }),
        withLatestFrom(
          state$.pipe(map((state: AppState) => state.Field.entities)),
          state$.pipe(map((state: AppState) => state.User.entities)),
          state$.pipe(map((state: AppState) => state.Measurement.phenomena)),
          state$.pipe(map((state: AppState) => state.Measurement.filterVisible)),
          state$.pipe(map((state: AppState) => state.Measurement.catalogCompanyId)),
          state$.pipe(map((state: AppState) => state.Field.lastPropertyId)),
          state$.pipe(map((state: AppState) => state.User.lastOrgId)),
          state$.pipe(map((state: AppState) => state.Measurement.isLoading))
        ),
        concatMap(
          ([
            result,
            fieldsState,
            usersListState,
            phenomenonsTreeState,
            filterVisible,
            lastCompanyLoadPhenomenonsTree,
            lastPropertyLoadFields,
            lastOrgLoadUsers,
            isLoading
          ]: any) => {
            const requests: any = [];
            if (!fieldsState || Object.values(fieldsState).length === 0 || lastPropertyLoadFields !== payload.propertyId) {
              requests.push(getFieldsByProperty(payload.propertyId));
            } else {
              requests.push(of(fieldsState));
            }
            if (!usersListState || Object.values(usersListState).length === 0 || lastOrgLoadUsers !== payload.companyId) {
              requests.push(getUsersByOrg({ orgId: payload.companyId }));
            } else {
              requests.push(of(usersListState));
            }
            if (!phenomenonsTreeState || lastCompanyLoadPhenomenonsTree !== payload.companyId) {
              requests.push(getMeasurementPhenomena(payload.companyId));
            } else {
              requests.push(of(phenomenonsTreeState));
            }
            requests.push(getRegionsByProperty(payload.propertyId));

            return forkJoin([of(result), of(filterVisible), of(isLoading), ...requests]);
          }
        ),
        concatMap(([result, filterVisible, isLoading, fieldsList, usersList, phenomenonsTreeList, regionsList]: any) => {
          const rangeKey = result.range_key;
          const arrayActions: any = [];

          let users;
          if (usersList.data) {
            users = usersList.data.content;
            arrayActions.push(UserActions.loadOrgUsersSuccess(users));
          } else {
            users = Object.values(usersList);
          }

          const phenomenonsTree = phenomenonsTreeList;
          arrayActions.push(actions.setPhenomena(payload.companyId, phenomenonsTree));

          let fields;
          if (fieldsList.data) {
            fields = fieldsList.data.content;
            arrayActions.push(FieldActions.loadFieldsByPropertySuccess(fields));
          } else {
            fields = Object.values(fieldsList);
          }

          let regions;
          if (regionsList) {
            regions = regionsList;
            arrayActions.push(RegionActions.loadRegionsSuccess(regions));
          } else {
            regions = Object.values(regionsList);
          }

          const measurementByInspectionEnabled = !!state$.value.App.systemFlags?.P40_60446_MEASUREMENT_MAINTENANCE_BY_INSPECTION;
          const mutableMeasurements = buildMeasurements(
            result.monitorings,
            phenomenonsTree,
            users,
            fields,
            payload.propertyId,
            regions,
            measurementByInspectionEnabled
          );

          if (!rangeKey || mutableMeasurements.length > 0) {
            arrayActions.push(actions.setRangeKey(rangeKey));
            if (payload.rangeKey) {
              arrayActions.push(actions.upsertMeasurementsByProperty(mutableMeasurements));
            } else {
              arrayActions.push(actions.loadMeasurementsByPropertySuccess(mutableMeasurements));
            }
          }

          if (filterVisible && !isLoading) {
            arrayActions.push(actions.toggleFilterModal());
          }

          arrayActions.push(UserActions.setLastOrgIdLoadUsers(payload.companyId));
          arrayActions.push(FieldActions.setLastPropertyIdLoadFields(payload.propertyId));

          return concat([...arrayActions]);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading Measurements!');
          return of(actions.loadMeasurementsByPropertyFailure(errorMsg));
        })
      );
    })
  );

export const handleEditValueMeasurements = (action$, state$) =>
  action$.pipe(
    ofType(actions.EDIT_MEASUREMENT_VALUE),
    map((action: Action<Measurement>) => action.payload),
    concatMap((measurement: Measurement): Observable<any> => {
      const measuresViaObservationServiceEnabled = !!state$.value.App.systemFlags?.P40_55417_MEASURES_VIA_OBSERVATION_SERVICE;
      const editMeasurementByInspectionEnabled = !!state$.value.App.systemFlags?.P40_60446_MEASUREMENT_MAINTENANCE_BY_INSPECTION;

      let editRequest;
      // The measures endpoint keys rows on inspection_group_id, so a measurement without one
      // would earn a guaranteed 422. Fall back to the previous path instead.
      if (measuresViaObservationServiceEnabled && measurement.inspectionGroupId) {
        editRequest = editMeasures(getEditMeasuresRequest(measurement));
      } else if (editMeasurementByInspectionEnabled) {
        editRequest = editMeasurementValueByInspection(getEditMeasurementByInspectionRequest(measurement));
      } else {
        editRequest = editMeasurementValue(getEditMeasurementRequest(measurement));
      }

      return editRequest.pipe(
        concatMap(() => {
          notification('success', 'Measurement was edited successfully!');
          return of(actions.editMeasurementValueSuccess(measurement), actions.toggleMeasurementModal(measurement.id));
        }),
        catchError((error: any) => {
          const measuresErrorMsg = getMeasuresErrorMessage(error);
          if (measuresErrorMsg) {
            notification('error', measuresErrorMsg);
            return of(actions.editMeasurementValueFailure(measuresErrorMsg));
          }
          const errorMsg = notifyErrorOrFallback(error, 'Error editing measurement!');
          return of(actions.editMeasurementValueFailure(errorMsg));
        })
      );
    })
  );

export const handleDeleteMeasurement = (action$, state$) =>
  action$.pipe(
    ofType(actions.DELETE_MEASUREMENT),
    map((action: Action<DeleteMeasurementRequestParams>) => action.payload),
    concatMap((deleteMeasurementRequestParams: DeleteMeasurementRequestParams) => {
      const measuresViaObservationServiceEnabled = !!state$.value.App.systemFlags?.P40_55417_MEASURES_VIA_OBSERVATION_SERVICE;
      const deleteMeasurementByInspectionEnabled = !!state$.value.App.systemFlags?.P40_60446_MEASUREMENT_MAINTENANCE_BY_INSPECTION;

      // Without an inspectionGroupId the v2 path would send `undefined` in the URL and earn a guaranteed 400.
      const canDeleteViaMeasures = measuresViaObservationServiceEnabled && !!deleteMeasurementRequestParams.inspectionGroupId;

      let deleteRequest;
      if (canDeleteViaMeasures) {
        deleteRequest = deleteMeasureByInspectionGroup({
          observationId: deleteMeasurementRequestParams.monitoringId,
          inspectionGroupId: deleteMeasurementRequestParams.inspectionGroupId,
          characteristicId: deleteMeasurementRequestParams.characteristicId
        });
      } else if (deleteMeasurementByInspectionEnabled) {
        deleteRequest = deleteMeasurementByInspection({
          propertyId: deleteMeasurementRequestParams.propertyId,
          observationId: deleteMeasurementRequestParams.monitoringId,
          inspectionId: deleteMeasurementRequestParams.inspectionId,
          characteristicId: deleteMeasurementRequestParams.characteristicId,
          startDate: deleteMeasurementRequestParams.startDate
        });
      } else {
        deleteRequest = deleteMeasurement(deleteMeasurementRequestParams);
      }

      return deleteRequest.pipe(
        concatMap(() => {
          notification('success', 'Measurement was deleted successfully!');
          const { measurementId } = deleteMeasurementRequestParams;
          return of(actions.deleteMeasurementSuccess(measurementId), actions.toggleMeasurementModal(measurementId));
        }),
        catchError((error: any) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error deleting measurement!');
          return of(actions.deleteMeasurementFailure(errorMsg));
        })
      );
    })
  );
